import Phaser from "phaser";
import { CANVAS_COLOURS, brushFootprint, type CanvasColour, type CellMark, type CellRect, type GridPoint, type TileBounds, type WorldRect } from "daedalus-core";

/**
 * Edit overlays (Playbook 5.9, P0.3-T2): a tile grid over the visible range, a marker on the cursor vertex
 * and a highlight on the brush footprint, plus the tile selection rectangle (ADR-044) and, while a move drag
 * is in progress, the outline of where it will land, and the marquee a Select drag on empty ground draws
 * (P1.6-T33), in world pixels, drawn as the tile selection is but with no lines between tiles, since it selects props, and each selected prop's box round its
 * art with a square handle at each corner (ADR-070's amendment of P1.6-T61), and the mark of the cell the inspector's "In this cell" list describes:
 * the selection's edge and keylines with no fill, or, while a line of the list is pointed at, Highlight layer's raspberry as edge and 25% fill. One `Graphics` object, redrawn only when the state it
 * depends on changes, never every frame. Reads no document grid; the footprint is vertex indices only. In Zones mode
 * the vertex cursor gives way to the tile-sized footprint of the stamp a press would paint (ADR-054's amendment of
 * 2026-10-01, P1.6-T65): the outline of whole tiles, drawn in the cursor's colour. An area across layers (ADR-128
 * decisions 13 and 41c) is its rectangle's edge, as the tile selection's with no lines between tiles since it takes
 * every layer, over a faint fill, in the selection's gold, or in the erase colour while it is an erase box. A
 * pending move of an area (decisions 52 to 55) is the source's dashed outline, the outline of where the block lands
 * over a faint fill, in gold or, refused at the map's edge, in the erase colour, the paint the move replaces tinted, the things that stand
 * there and stay outlined, and a faint cross on the map's centre; every stroke keeps its screen width at any zoom,
 * so a town forty pixels across still reads as a block.
 */

/**
 * A pending move of an area as the overlay draws it: the source and the destination in tiles, whether the move is
 * refused, the paint the move replaces as boxes in tiles, the boxes of the things that stand at the destination and stay,
 * in world pixels, and the map's centre in tiles.
 */
export interface AreaMoveOverlay {
  readonly source: CellRect;
  readonly destination: CellRect;
  readonly refused: boolean;
  readonly replaced: readonly { readonly x: number; readonly y: number; readonly w: number; readonly h: number }[];
  readonly kept: readonly WorldRect[];
  readonly centre: { readonly x: number; readonly y: number };
}

/**
 * What the overlay draws.
 */
export interface OverlayState {
  readonly gridVisible: boolean;
  readonly cursor: GridPoint | null;
  readonly brushSize: number;
  /** The tiles a zone stamp would paint under the pointer, drawn in place of the vertex cursor while not null. */
  readonly tileCursor?: TileBounds | null;
  readonly selection?: CellRect | null;
  readonly selectionOffset?: { readonly dx: number; readonly dy: number } | null;
  readonly marquee?: WorldRect | null;
  /** The area across layers that stands or is being drawn, in tiles, and whether it is an erase box. */
  readonly area?: { readonly rect: CellRect; readonly erase: boolean } | null;
  /** The pending move of the area, drawn in place of `area` while one is pending. The object is compared by identity, so a caller hands the same one until something in it changes. */
  readonly areaMove?: AreaMoveOverlay | null;
  readonly propBoxes?: readonly WorldRect[];
  /** The props and markers whose selection waits for its own mode: each box in the selection's edge, dimmed, with no handles. */
  readonly asideBoxes?: readonly WorldRect[];
  /** The cell the inspector's "In this cell" list describes, or the thing a line of it means: drawn last, over everything else here. */
  readonly cellMark?: CellMark | null;
  /** What the select tool would take under the pointer: the left button's target outlined in the selection's colour and, where the right button would take another thing, that thing in the erase colour. */
  readonly pressHover?: { readonly selectBox: WorldRect | null; readonly eraseBox: WorldRect | null } | null;
}

/**
 * The visible world rectangle and zoom the overlay draws for.
 */
export interface OverlayView {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly zoom: number;
}

const GRID = CANVAS_COLOURS.grid;
const CURSOR = CANVAS_COLOURS.cursor;
const FOOTPRINT_ALPHA = 0.35;
const CURSOR_RADIUS = 4;
const SELECTION = CANVAS_COLOURS.tileSelection;
const SELECTION_OUTLINE = CANVAS_COLOURS.tileSelectionOutline;
const SELECTION_EDGE = 2;
const SELECTION_KEYLINE = 1;
const SELECTION_INNER = 1;
const SELECTION_INNER_ALPHA = 0.5;
const SELECTION_FILL_ALPHA = 0.25;
const SELECTION_GHOST_ALPHA = 0.6;
const MARQUEE = CANVAS_COLOURS.marquee;
const MARQUEE_OUTLINE = CANVAS_COLOURS.marqueeOutline;
const PROP_BOX = CANVAS_COLOURS.selectedProp;
const PROP_BOX_OUTLINE = CANVAS_COLOURS.selectedPropOutline;
const AREA_ERASE = CANVAS_COLOURS.brushErase;
const PRESS_HOVER_ALPHA = 0.7;
const ASIDE_ALPHA = 0.4;
const AREA_FILL_ALPHA = 0.12;
const AREA_TINT_ALPHA = 0.8;
const AREA_TINT_LEAST = 8;
const AREA_KEPT_ALPHA = 0.8;
const AREA_DASH = 8;
const AREA_CROSS_ARM = 28;
const AREA_CROSS_ALPHA = 0.6;
const HANDLE_SIZE = 5;
const CELL_HOVER = CANVAS_COLOURS.layerHighlight;
const CELL_HOVER_OUTLINE = CANVAS_COLOURS.layerHighlightKeyline;

/**
 * Draws the grid, cursor and footprint.
 */
export class EditOverlay {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private last: string = "";
  private tileCursorDrawn: TileBounds | null = null;
  private moveDrawn: AreaMoveOverlay | null = null;
  private moveSerial = 0;

  /**
   * @param scene Scene to draw into.
   * @param tileSize Tile size in pixels.
   * @param width Map width in tiles.
   * @param height Map height in tiles.
   * @param depth Depth above every chunk layer.
   */
  public constructor(
    scene: Phaser.Scene,
    private readonly tileSize: number,
    private readonly width: number,
    private readonly height: number,
    depth: number,
  ) {
    this.graphics = scene.add.graphics().setDepth(depth);
  }

  /**
   * Redraws when the view or state changed since the last call.
   *
   * @param view Visible world rectangle and zoom.
   * @param state Grid, cursor and brush.
   * @returns True when a redraw happened.
   */
  public sync(view: OverlayView, state: OverlayState): boolean {
    const move = state.areaMove ?? null;
    if (move !== this.moveDrawn) {
      this.moveDrawn = move;
      this.moveSerial += 1;
    }
    const signature = `${this.moveSerial},${Math.floor(view.left)},${Math.floor(view.top)},${Math.ceil(view.width)},${Math.ceil(view.height)},${view.zoom},${state.gridVisible},${state.cursor?.x ?? "-"},${state.cursor?.y ?? "-"},${state.brushSize},${EditOverlay.tileCursorKey(state.tileCursor ?? null)},${EditOverlay.rectKey(state.selection ?? null)},${state.selectionOffset?.dx ?? "-"},${state.selectionOffset?.dy ?? "-"},${EditOverlay.marqueeKey(state.marquee ?? null)},${EditOverlay.rectKey(state.area?.rect ?? null)}:${state.area?.erase === true},${EditOverlay.boxesKey(state.propBoxes ?? [])},${EditOverlay.boxesKey(state.asideBoxes ?? [])},${EditOverlay.markKey(state.cellMark ?? null)},${EditOverlay.marqueeKey(state.pressHover?.selectBox ?? null)},${EditOverlay.marqueeKey(state.pressHover?.eraseBox ?? null)}`;
    if (signature === this.last) {
      return false;
    }
    this.last = signature;
    this.draw(view, state);
    return true;
  }

  /**
   * Draws everything from scratch.
   *
   * @param view Visible world rectangle and zoom.
   * @param state Grid, cursor and brush.
   */
  private draw(view: OverlayView, state: OverlayState): void {
    const g = this.graphics;
    const size = this.tileSize;
    const lineWidth = 1 / view.zoom;
    g.clear();
    this.tileCursorDrawn = state.tileCursor ?? null;
    if (state.gridVisible) {
      g.lineStyle(lineWidth, GRID.rgb, GRID.alpha);
      const firstColumn = Math.max(0, Math.floor(view.left / size));
      const lastColumn = Math.min(this.width, Math.ceil((view.left + view.width) / size));
      const firstRow = Math.max(0, Math.floor(view.top / size));
      const lastRow = Math.min(this.height, Math.ceil((view.top + view.height) / size));
      for (let column = firstColumn; column <= lastColumn; column += 1) {
        g.lineBetween(column * size, firstRow * size, column * size, lastRow * size);
      }
      for (let row = firstRow; row <= lastRow; row += 1) {
        g.lineBetween(firstColumn * size, row * size, lastColumn * size, row * size);
      }
    }
    const tiles = state.tileCursor ?? null;
    if (tiles !== null) {
      const x = tiles.x0 * size;
      const y = tiles.y0 * size;
      const w = (tiles.x1 - tiles.x0 + 1) * size;
      const h = (tiles.y1 - tiles.y0 + 1) * size;
      g.fillStyle(CURSOR.rgb, CURSOR.alpha * FOOTPRINT_ALPHA);
      g.fillRect(x, y, w, h);
      g.lineStyle(2 / view.zoom, CURSOR.rgb, CURSOR.alpha);
      g.strokeRect(x, y, w, h);
    } else if (state.cursor !== null) {
      g.fillStyle(CURSOR.rgb, CURSOR.alpha * FOOTPRINT_ALPHA);
      for (const vertex of brushFootprint(state.cursor.x, state.cursor.y, state.brushSize, this.width, this.height)) {
        g.fillCircle(vertex.x * size, vertex.y * size, CURSOR_RADIUS / view.zoom + 2);
      }
      g.lineStyle(2 / view.zoom, CURSOR.rgb, CURSOR.alpha);
      g.strokeCircle(state.cursor.x * size, state.cursor.y * size, CURSOR_RADIUS / view.zoom + 2);
    }
    const selection = state.selection ?? null;
    if (selection !== null) {
      const x = selection.x0 * size;
      const y = selection.y0 * size;
      const w = (selection.x1 - selection.x0 + 1) * size;
      const h = (selection.y1 - selection.y0 + 1) * size;
      g.fillStyle(SELECTION.rgb, SELECTION.alpha * SELECTION_FILL_ALPHA);
      g.fillRect(x, y, w, h);
      EditOverlay.strokeSelection(g, x, y, w, h, view.zoom, 1);
      g.lineStyle(SELECTION_INNER / view.zoom, SELECTION.rgb, SELECTION.alpha * SELECTION_INNER_ALPHA);
      for (let column = selection.x0 + 1; column <= selection.x1; column += 1) {
        g.lineBetween(column * size, y, column * size, y + h);
      }
      for (let row = selection.y0 + 1; row <= selection.y1; row += 1) {
        g.lineBetween(x, row * size, x + w, row * size);
      }
      const offset = state.selectionOffset ?? null;
      if (offset !== null && (offset.dx !== 0 || offset.dy !== 0)) {
        EditOverlay.strokeSelection(g, x + offset.dx * size, y + offset.dy * size, w, h, view.zoom, SELECTION_GHOST_ALPHA);
      }
    }
    const marquee = state.marquee ?? null;
    if (marquee !== null) {
      const x = Math.min(marquee.left, marquee.right);
      const y = Math.min(marquee.top, marquee.bottom);
      const w = Math.abs(marquee.right - marquee.left);
      const h = Math.abs(marquee.bottom - marquee.top);
      g.fillStyle(MARQUEE.rgb, MARQUEE.alpha * SELECTION_FILL_ALPHA);
      g.fillRect(x, y, w, h);
      EditOverlay.strokeSelection(g, x, y, w, h, view.zoom, 1, MARQUEE, MARQUEE_OUTLINE);
    }
    const move = state.areaMove ?? null;
    if (move !== null) {
      this.drawAreaMove(move, view);
    }
    const area = move === null ? (state.area ?? null) : null;
    if (area !== null) {
      const colour = area.erase ? AREA_ERASE : SELECTION;
      const x = area.rect.x0 * size;
      const y = area.rect.y0 * size;
      const w = (area.rect.x1 - area.rect.x0 + 1) * size;
      const h = (area.rect.y1 - area.rect.y0 + 1) * size;
      g.fillStyle(colour.rgb, colour.alpha * AREA_FILL_ALPHA);
      g.fillRect(x, y, w, h);
      EditOverlay.strokeSelection(g, x, y, w, h, view.zoom, 1, colour);
    }
    for (const box of state.propBoxes ?? []) {
      EditOverlay.strokePropBox(g, box, view.zoom);
    }
    for (const box of state.asideBoxes ?? []) {
      EditOverlay.strokeSelection(g, box.left, box.top, box.right - box.left, box.bottom - box.top, view.zoom, ASIDE_ALPHA, PROP_BOX, PROP_BOX_OUTLINE);
    }
    const hover = state.pressHover ?? null;
    if (hover !== null && hover.selectBox !== null) {
      const box = hover.selectBox;
      EditOverlay.strokeSelection(g, box.left, box.top, box.right - box.left, box.bottom - box.top, view.zoom, PRESS_HOVER_ALPHA);
    }
    if (hover !== null && hover.eraseBox !== null) {
      const box = hover.eraseBox;
      EditOverlay.strokeSelection(g, box.left, box.top, box.right - box.left, box.bottom - box.top, view.zoom, PRESS_HOVER_ALPHA, AREA_ERASE);
    }
    const mark = state.cellMark ?? null;
    if (mark !== null && mark.look === "hover") {
      g.fillStyle(CELL_HOVER.rgb, CELL_HOVER.alpha * SELECTION_FILL_ALPHA);
      g.fillRect(mark.x, mark.y, mark.w, mark.h);
      EditOverlay.strokeSelection(g, mark.x, mark.y, mark.w, mark.h, view.zoom, 1, CELL_HOVER, CELL_HOVER_OUTLINE);
    } else if (mark !== null) {
      EditOverlay.strokeSelection(g, mark.x, mark.y, mark.w, mark.h, view.zoom, 1);
    }
  }

  /**
   * Draws a pending move of an area: the cross on the map's centre, the source's dashed outline, the destination's
   * outline over its faint fill, the paint it replaces tinted and the kept things outlined. Each tinted box is at
   * least `AREA_TINT_LEAST` screen pixels a side, about its own middle, over a keyline drawn first, so boxes side by
   * side read as one rose patch with a dark edge and a few replaced quarter cells of a town still show with the
   * whole town in view.
   *
   * @param move The move.
   * @param view The visible world rectangle and zoom.
   */
  private drawAreaMove(move: AreaMoveOverlay, view: OverlayView): void {
    const { zoom } = view;
    const g = this.graphics;
    const size = this.tileSize;
    const colour = move.refused ? AREA_ERASE : SELECTION;
    const cx = move.centre.x * size;
    const cy = move.centre.y * size;
    const arm = AREA_CROSS_ARM / zoom;
    g.lineStyle(3 / zoom, SELECTION_OUTLINE.rgb, SELECTION_OUTLINE.alpha * AREA_CROSS_ALPHA);
    g.lineBetween(cx - arm, cy, cx + arm, cy);
    g.lineBetween(cx, cy - arm, cx, cy + arm);
    g.lineStyle(1 / zoom, SELECTION.rgb, SELECTION.alpha * AREA_CROSS_ALPHA);
    g.lineBetween(cx - arm, cy, cx + arm, cy);
    g.lineBetween(cx, cy - arm, cx, cy + arm);
    EditOverlay.strokeDashed(g, move.source.x0 * size, move.source.y0 * size, (move.source.x1 - move.source.x0 + 1) * size, (move.source.y1 - move.source.y0 + 1) * size, view);
    const x = move.destination.x0 * size;
    const y = move.destination.y0 * size;
    const w = (move.destination.x1 - move.destination.x0 + 1) * size;
    const h = (move.destination.y1 - move.destination.y0 + 1) * size;
    g.fillStyle(colour.rgb, colour.alpha * AREA_FILL_ALPHA);
    g.fillRect(x, y, w, h);
    const least = AREA_TINT_LEAST / zoom;
    const tints = move.replaced.map((box) => {
      const tw = Math.max(box.w * size, least);
      const th = Math.max(box.h * size, least);
      return { x: (box.x + box.w / 2) * size - tw / 2, y: (box.y + box.h / 2) * size - th / 2, w: tw, h: th };
    });
    g.lineStyle((2 * SELECTION_KEYLINE) / zoom, SELECTION_OUTLINE.rgb, SELECTION_OUTLINE.alpha);
    for (const tint of tints) {
      g.strokeRect(tint.x, tint.y, tint.w, tint.h);
    }
    g.fillStyle(AREA_ERASE.rgb, AREA_ERASE.alpha * AREA_TINT_ALPHA);
    for (const tint of tints) {
      g.fillRect(tint.x, tint.y, tint.w, tint.h);
    }
    g.lineStyle(1 / zoom, SELECTION.rgb, SELECTION.alpha * AREA_KEPT_ALPHA);
    for (const box of move.kept) {
      g.strokeRect(box.left, box.top, box.right - box.left, box.bottom - box.top);
    }
    EditOverlay.strokeSelection(g, x, y, w, h, zoom, 1, colour);
  }

  /**
   * Strokes a rectangle's edge in dashes of `AREA_DASH` screen pixels, gold over a keyline, for the place a pending
   * move leaves. Only the dashes inside the view are drawn, so a rectangle far larger than the view costs no more
   * than the view's own edge.
   *
   * @param g The graphics to draw into.
   * @param x The rectangle's left edge in world pixels.
   * @param y Its top edge.
   * @param w Its width.
   * @param h Its height.
   * @param view The visible world rectangle and zoom.
   */
  private static strokeDashed(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, view: OverlayView): void {
    const dash = AREA_DASH / view.zoom;
    const period = 2 * dash;
    /**
     * The first and last dash starts along an edge that the view shows.
     *
     * @param from Where the edge starts on its axis.
     * @param length The edge's length.
     * @param low The view's low bound on that axis.
     * @param high The view's high bound.
     * @returns The first start and the bound past the last, as distances along the edge.
     */
    const shown = (from: number, length: number, low: number, high: number): [number, number] => [Math.max(0, Math.floor((low - from) / period) * period), Math.min(length, high - from)];
    const [x0, x1] = shown(x, w, view.left, view.left + view.width);
    const [y0, y1] = shown(y, h, view.top, view.top + view.height);
    for (const [width, colour] of [[3, SELECTION_OUTLINE], [1, SELECTION]] as const) {
      g.lineStyle(width / view.zoom, colour.rgb, colour.alpha);
      for (let at = x0; at < x1; at += period) {
        const end = Math.min(w, at + dash);
        g.lineBetween(x + at, y, x + end, y);
        g.lineBetween(x + at, y + h, x + end, y + h);
      }
      for (let at = y0; at < y1; at += period) {
        const end = Math.min(h, at + dash);
        g.lineBetween(x, y + at, x, y + end);
        g.lineBetween(x + w, y + at, x + w, y + end);
      }
    }
  }

  /**
   * Draws a selected prop's box as the design's map selection is drawn: the selection's edge round the art,
   * then at each corner a `HANDLE_SIZE` screen-pixel square in `gold` inside a 1 px keyline. The handles mark the
   * selection and resize nothing.
   *
   * @param g The graphics to draw into.
   * @param box The prop's art in world pixels.
   * @param zoom The camera's zoom.
   */
  private static strokePropBox(g: Phaser.GameObjects.Graphics, box: WorldRect, zoom: number): void {
    const w = box.right - box.left;
    const h = box.bottom - box.top;
    EditOverlay.strokeSelection(g, box.left, box.top, w, h, zoom, 1, PROP_BOX, PROP_BOX_OUTLINE);
    const size = HANDLE_SIZE / zoom;
    const ring = SELECTION_KEYLINE / zoom;
    for (const [x, y] of [[box.left, box.top], [box.right, box.top], [box.left, box.bottom], [box.right, box.bottom]] as const) {
      g.fillStyle(PROP_BOX_OUTLINE.rgb, PROP_BOX_OUTLINE.alpha);
      g.fillRect(x - size / 2 - ring, y - size / 2 - ring, size + 2 * ring, size + 2 * ring);
      g.fillStyle(PROP_BOX.rgb, PROP_BOX.alpha);
      g.fillRect(x - size / 2, y - size / 2, size, size);
    }
  }

  /**
   * The part of the redraw signature that the selected props' boxes contribute.
   *
   * @param boxes The boxes.
   * @returns A short stable string.
   */
  private static boxesKey(boxes: readonly WorldRect[]): string {
    let key = "";
    for (const box of boxes) {
      key += `${box.left}:${box.top}:${box.right}:${box.bottom};`;
    }
    return key;
  }

  /**
   * Strokes a selected area's edge as the design's Overlay colours card does (`1790408874-42c2`, ADR-070's
   * amendment): the gold edge, `SELECTION_EDGE` screen pixels wide and centred on the area's border, with a
   * `SELECTION_KEYLINE` pixel keyline in `tileSelectionOutline` just outside it and another just inside it,
   * so the edge holds on light ground and against the fill. The keylines sit beside the gold, never under
   * it, so a translucent edge (the outline of where a moved selection lands) keeps its colour. The inside
   * keyline is left out of an area too thin to hold it. Both keep their screen width at any zoom.
   *
   * @param g The graphics to draw into.
   * @param x The area's left edge in world pixels.
   * @param y The area's top edge in world pixels.
   * @param w The area's width in world pixels.
   * @param h The area's height in world pixels.
   * @param zoom The camera's zoom.
   * @param opacity How much of each colour's own alpha to draw, from 0 to 1.
   * @param edge The edge's colour, the tile selection's unless given.
   * @param keyline The keyline's colour, the tile selection's unless given.
   */
  private static strokeSelection(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, zoom: number, opacity: number, edge: CanvasColour = SELECTION, keyline: CanvasColour = SELECTION_OUTLINE): void {
    const offset = (SELECTION_EDGE / 2 + SELECTION_KEYLINE / 2) / zoom;
    g.lineStyle(SELECTION_KEYLINE / zoom, keyline.rgb, keyline.alpha * opacity);
    g.strokeRect(x - offset, y - offset, w + 2 * offset, h + 2 * offset);
    if (w > 2 * offset && h > 2 * offset) {
      g.strokeRect(x + offset, y + offset, w - 2 * offset, h - 2 * offset);
    }
    g.lineStyle(SELECTION_EDGE / zoom, edge.rgb, edge.alpha * opacity);
    g.strokeRect(x, y, w, h);
  }

  /**
   * The part of the redraw signature that the marquee contributes.
   *
   * @param rect The marquee, or null.
   * @returns A short stable string.
   */
  private static marqueeKey(rect: WorldRect | null): string {
    return rect === null ? "-" : `${rect.left}:${rect.top}:${rect.right}:${rect.bottom}`;
  }

  /**
   * The part of the redraw signature that the cell mark contributes.
   *
   * @param mark The mark, or null.
   * @returns A short stable string.
   */
  private static markKey(mark: CellMark | null): string {
    return mark === null ? "-" : `${mark.x}:${mark.y}:${mark.w}:${mark.h}:${mark.look}`;
  }

  /**
   * The part of the redraw signature that a rectangle contributes.
   *
   * @param rect The rectangle, or null.
   * @returns A short stable string.
   */
  private static rectKey(rect: CellRect | null): string {
    return rect === null ? "-" : `${rect.x0}:${rect.y0}:${rect.x1}:${rect.y1}`;
  }

  /**
   * The tiles the overlay last drew its zone stamp cursor over, in tile columns and rows, or null while it drew
   * the vertex cursor or none.
   *
   * @returns The bounds, or null.
   */
  public get tileCursor(): TileBounds | null {
    return this.tileCursorDrawn;
  }

  /**
   * The part of the redraw signature that the zone stamp cursor contributes.
   *
   * @param tiles The cursor's tiles, or null.
   * @returns A short stable string.
   */
  private static tileCursorKey(tiles: TileBounds | null): string {
    return tiles === null ? "-" : `${tiles.x0}:${tiles.y0}:${tiles.x1}:${tiles.y1}`;
  }

  /**
   * Removes the overlay from the scene.
   */
  public destroy(): void {
    this.graphics.destroy();
  }
}
